/**
 * History › Managers — built to turn `16a`.
 *
 * Ten managers, their current team, where they finished, and what they have won. The drawn
 * screen makes one strong claim and this build keeps it:
 *
 *   BLANK BEATS PARTIAL. Titles and career W–L stay empty while any season still has a team
 *   nobody can name, because a fifteen-season total sitting under a column headed "career" is a
 *   number somebody will quote. The five unpaired identities sit directly beneath the table —
 *   not in settings — so the fix is visible from the screen that is missing the data, and the
 *   columns fill themselves in the moment the last one is paired.
 *
 * The pairing tool itself is the existing admin screen, folded in below rather than living on a
 * tab of its own: this page is where you find out the record is incomplete, so it is where the
 * repair belongs.
 */

import { useMemo, useState } from 'react';
import standings from '../data/standings-history.json';
import { gmFor, identityFor, managerName } from '../domain/identity.js';
import { careerTable } from '../domain/records.js';
import { managerMoney } from '../domain/payouts.js';
import { usePairingVersion } from './pairings.js';
import { GMS } from '../store/session.js';
import { GmBadge } from './GmBadge.js';
import { TeamPairing } from './TeamPairing.js';
import { Linkify } from './RuleText.js';
import type { LeagueStore, Role } from '../store/types.js';

interface Row { rank: number; team: string; gm: string | null; w: number; l: number; gmResolved?: boolean }
interface SeasonFile { season: number; standings: Row[] }
const SEASONS = (standings as unknown as { seasons: SeasonFile[] }).seasons;
const LATEST = Math.max(...SEASONS.map((s) => s.season));

export function HistoryManagers(
  { gm, role, store }: { gm: string; role: Role; store: LeagueStore },
) {
  // Pairing rewrites this page: repaint when the overlay changes.
  usePairingVersion();
  const [pairing, setPairing] = useState(false);
  const me = gm.toUpperCase();

  /** Every team, in any season, the league still cannot put a name to. */
  const unpaired = useMemo(() => SEASONS.flatMap((s) => s.standings
    .filter((r) => !gmFor(s.season, r))
    .map((r) => ({ season: s.season, team: r.team }))
    .sort((a, b) => b.season - a.season)), []);

  /*
   * A TEAM NOBODY HAS NAMED IS ITS OWN MANAGER (Tim, 8 September: "pretend the unpaired 2003-2006
   * identities are standalone identities carrying their own team name, likely never to be
   * paired").
   *
   * The page used to hold both columns blank while ANY season had an unnamed team, on the
   * principle that a career total quietly skipping six seasons is a number somebody will quote.
   * The principle was right and the premise was wrong: those five teams are not gaps in somebody
   * else's record, they are five more records. Nothing is being skipped, so nothing needs
   * withholding — the ten men get their full careers and the five teams get lines of their own,
   * marked as what they are.
   *
   * They sort to the bottom because none of them played in the current season, which is what the
   * finish column is ordered by; that falls out of the sort rather than being arranged.
   */
  const standalone = useMemo(() => {
    const seen = new Map<string, string>();
    for (const s of SEASONS) {
      for (const r of s.standings) {
        if (gmFor(s.season, r)) continue;
        const id = identityFor(s.season, r);
        if (!seen.has(id.toUpperCase())) seen.set(id.toUpperCase(), r.team);
      }
    }
    return [...seen.entries()].map(([id, team]) => ({ id, team }));
  }, []);

  const rows = useMemo(() => {
    const latest = SEASONS.find((s) => s.season === LATEST);
    const careers = careerTable().rows;
    const line = (id: string, label: string, unnamed: boolean) => {
      const seat = latest?.standings
        .find((r) => identityFor(LATEST, r).toUpperCase() === id.toUpperCase());
      const career = careers.find((c) => c.gm.toUpperCase() === id.toUpperCase());
      return {
        id,
        label,
        unnamed,
        team: seat?.team ?? null,
        finish: seat?.rank ?? null,
        titles: managerMoney(id).titles,
        record: career ? `${career.w}–${career.l}${career.t ? `–${career.t}` : ''}` : null,
      };
    };
    return [
      ...GMS.map((g) => line(g, managerName(g), false)),
      /* His own team name, spelled the way the archive spells it — `still not a club` is lower
         case because that is how the man typed it, and shouting it back is not a correction. */
      ...standalone.map((u) => line(u.id, u.team, true)),
    ].sort((a, b) => (a.finish ?? 99) - (b.finish ?? 99) || a.label.localeCompare(b.label));
  }, [standalone]);

  return (
    <div className="app">
      <Linkify>
        <header className="pagehead">
          <div>
            <h1>Managers</h1>
            <p className="sub">
              {GMS.length} active
              {standalone.length > 0 && (
                <>
                  , plus {standalone.length} known only by team name
                  {' '}from {Math.min(...unpaired.map((u) => u.season))} to{' '}
                  {Math.max(...unpaired.map((u) => u.season))}
                </>
              )}
            </p>
          </div>
          {unpaired.length > 0 && (
            <button type="button" className="btn" onClick={() => setPairing((v) => !v)}>
              {pairing ? 'Hide the pairing tool' : 'Pair an identity'}
            </button>
          )}
        </header>

        <section className="card standingscard">
          <table className="standings managers">
            <thead>
              <tr>
                <th>Manager</th>
                <th>{LATEST} team</th>
                <th className="num">Finish</th>
                <th className="num">Titles</th>
                <th className="num">Career W–L</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={`${r.id === me ? 'mine' : ''}${r.unnamed ? ' unnamed' : ''}`}>
                  <td className="mgr">
                    {/*
                      A standalone identity has no badge, because a badge is a person's colour and
                      a hue invented for a team would read as a manager the league has forgotten.
                      It gets its team name and a mark saying that is all anybody knows.
                    */}
                    {r.unnamed
                      ? <><b>{r.label}</b> <span className="mut tag">team only</span></>
                      : <><GmBadge gm={r.id} /> <b>{r.label}</b></>}
                  </td>
                  <td className="team">
                    {r.team ?? <span className="none">not in {LATEST}</span>}
                  </td>
                  <td className="num">{r.finish ?? <span className="none">—</span>}</td>
                  <td className="num">{r.titles ?? <span className="none">—</span>}</td>
                  <td className="num">{r.record ?? <span className="none">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {standalone.length > 0 && (
            <p className="railfoot">
              Titles and career record run across all {SEASONS.length} seasons. The
              {' '}{standalone.length} team{standalone.length === 1 ? '' : 's'} marked
              {' '}<i>team only</i> {standalone.length === 1 ? 'is' : 'are'} from the years Yahoo
              kept no manager names — they count as their own managers rather than as gaps in
              anybody else&rsquo;s, which is what lets every career above be a complete one.
              Pairing one still moves that season&rsquo;s results across.
            </p>
          )}
        </section>

        {unpaired.length > 0 && (
          <section className="card unpairedcard identities">
            <div className="striptop">
              <h3 className="striphead">
                {unpaired.length} season{unpaired.length === 1 ? '' : 's'} recorded under a team name
              </h3>
              <span className="mut">pairing one moves that season&rsquo;s results across</span>
            </div>
            <div className="idchips">
              {unpaired.map((u) => (
                <span className="idchip" key={`${u.season}-${u.team}`}>
                  <span className="yr">{u.season}</span>{u.team}
                </span>
              ))}
            </div>
          </section>
        )}

        {/*
          The repair, on the screen that reports the damage. Admin-gated inside the tool itself —
          a wrong pairing does not break anything visibly, it quietly rewrites twenty years of
          career records.
        */}
        {pairing && (
          <div className="pairingfold">
            {/* Rendered bare, not inside a card: it is a full screen of its own and nesting a
                page inside a card doubles every margin on it. */}
            <TeamPairing gm={gm} role={role} store={store} />
          </div>
        )}
      </Linkify>
    </div>
  );
}
